import isNil from 'lodash/isNil';
import clsx from 'clsx';
import React, {
  ChangeEvent,
  useContext,
} from 'react';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from 'pages/Dashboard/components/Input/Radio';
import {
  WebcamContext,
  WebcamContextProps,
} from 'views/EMR/components/WebcamScanDialog/webcamContext';
import Button from 'pages/Dashboard/components/Button';
import {
  pluralize,
} from '@populateui/utils';
import ImageCarousel from 'views/EMR/components/WebcamScanDialog/ImageCarousel';

type OutputFormat = 'images' | 'pdf';

export default function PreviewStep() {
  const {
    photoCaptures,
    setPhotoCaptures,
    setWebcamScanStep,
    outputFormat,
    setOutputFormat,
    outputMode,
    multiple,
  } = useContext<WebcamContextProps>(WebcamContext);

  const photoCount = photoCaptures?.filter((src) => !isNil(src)).length ?? 0;
  const showOutputFormatSelector = multiple && outputMode === 'select';

  return (
    <div className="flex flex-col gap-form-row">
      {/* Captured pictures section */}
      <div className="flex flex-col gap-3">
        <div className="flex items-end justify-between">
          <h2 className="text-base font-semibold text-gray-900">
            Captured
            {' '}
            {pluralize('picture', photoCount, false)}
            {photoCount > 1 && (
              ` (${photoCount})`
            )}
          </h2>
          <Button
            type="button"
            variant="text"
            color="primary"
            onClick={() => { setWebcamScanStep('capture'); }}
            className="!p-0 !text-blue-500 hover:!text-blue-600 text-sm font-medium cursor-pointer !underline !underline-offset-2"
          >
            {multiple ? 'Add more' : 'Update'}
          </Button>
        </div>
        <div className="flex flex-col gap-1 overflow-auto flex-1">
          <ImageCarousel
            images={photoCaptures ?? []}
            onDelete={(src) => {
              setPhotoCaptures((prev) => prev.filter((p) => p !== src));
            }}
            onEdit={(idx, [edited]) => {
              if (!isNil(edited)) {
                const current = photoCaptures?.[idx];
                if (!isNil(current)) {
                  const blobUrl = edited.file ? URL.createObjectURL(edited.file) : null;
                  const next = blobUrl ?? edited.dataUrl ?? current;
                  setPhotoCaptures((prev) => prev?.map((p, i) => (i === idx ? next : p)) ?? []);
                }
              }
            }}
          />
        </div>
      </div>

      {/* Output format section */}
      {showOutputFormatSelector && (
        <div className="flex flex-col gap-form-row">
          <div className="flex flex-col">
            <h2 className="text-base font-semibold text-gray-900">
              Output format
            </h2>
            <p className="text-xs text-gray-500 font-medium">
              Choose whether to combine all pictures into a PDF or keep them as individual images.
            </p>
          </div>
          <RadioGroup
            value={outputFormat}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setOutputFormat(e.target.value as OutputFormat);
            }}
            row
            className="flex gap-form-field"
          >
            <Radio
              value="images"
              label={(
                <div className="flex flex-col w-full">
                  <div className="text-sm font-semibold text-gray-900">
                    Images
                  </div>
                  <div className="text-xs text-gray-500 text-left font-medium">
                    Save each picture as its own image file.
                  </div>
                </div>
              )}
              className={clsx(
                'p-2 border rounded-lg transition-colors cursor-pointer flex-1 !ml-0 !items-start [&_.MuiRadio-root]:hidden',
                {
                  'border-blue-500 bg-blue-50 ring-2 ring-blue-300': outputFormat === 'images',
                  'border-gray-200 hover:border-gray-300': outputFormat !== 'images',
                },
              )}
              classes={{
                checked: '!text-blue-500',
              }}
            />
            <Radio
              value="pdf"
              label={(
                <div className="flex flex-col w-full">
                  <div className="text-sm font-semibold text-gray-900">
                    PDF
                  </div>
                  <div className="text-xs text-gray-500 text-left font-medium">
                    Merge all captured pictures into one PDF document.
                  </div>
                </div>
              )}
              className={clsx(
                'p-2 border rounded-lg transition-colors cursor-pointer flex-1 !ml-0 !items-start [&_.MuiRadio-root]:hidden',
                {
                  'border-blue-500 bg-blue-50 ring-2 ring-blue-300': outputFormat === 'pdf',
                  'border-gray-200 hover:border-gray-300': outputFormat !== 'pdf',
                },
              )}
              classes={{
                checked: '!text-blue-500',
              }}
            />
          </RadioGroup>
        </div>
      )}
    </div>
  );
}
